import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Rss, Play, Trash2, Eye, EyeOff, ExternalLink, Loader2, RefreshCw, MessageSquare, Flag, Heart, CopyX, Share2, X, Link2, PenLine, Sparkles, Ban, Lock } from "lucide-react";

export default function AdminPressReview() {
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [onlyReported, setOnlyReported] = useState(true);
  const [stats, setStats] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [genMode, setGenMode] = useState("url");
  const [genUrl, setGenUrl] = useState("");
  const [genDesc, setGenDesc] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genInstr, setGenInstr] = useState("");
  const [genRemember, setGenRemember] = useState(false);
  const [newBlocked, setNewBlocked] = useState("");
  const [newRecommended, setNewRecommended] = useState("");
  const [reillustrating, setReillustrating] = useState(false);
  const [reJob, setReJob] = useState(null);
  const [recStats, setRecStats] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadPreview = () => {
    if (!cfg) return;
    setPreviewLoading(true);
    api.post("/admin/press-review/preview-effects", {
      illustrate_enabled: !!cfg.illustrate_enabled,
      illustrate_strength: cfg.illustrate_strength ?? 0.5,
      watercolor_enabled: !!cfg.watercolor_enabled,
      watercolor_strength: cfg.watercolor_strength ?? 0.5,
    }).then((r) => setPreview(r.data)).catch(() => {}).finally(() => setPreviewLoading(false));
  };

  const loadRecStats = () => {
    api.get("/admin/press-review/recommended-stats")
      .then((r) => setRecStats(r.data.counts || {}))
      .catch(() => {});
  };

  const loadComments = (reported = onlyReported) => {
    api.get(`/admin/press-review/comments?reported=${reported}`)
      .then((r) => setComments(r.data.items || []))
      .catch(() => {});
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/press-review/settings"),
      api.get("/admin/press-review"),
    ]).then(([s, l]) => {
      setCfg(s.data);
      setItems(l.data.items || []);
    }).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
    loadComments();
    api.get("/admin/press-review/stats?days=30").then((r) => setStats(r.data)).catch(() => {});
    loadRecStats();
    api.get("/admin/press-review/reillustrate-status").then((r) => {
      setReJob(r.data);
      if (r.data.running) { setReillustrating(true); setTimeout(pollReillustrate, 4000); }
    }).catch(() => {});
  };
  useEffect(loadAll, []);

  // Aperçu « avant/après » en direct : recalcule 600ms après le dernier changement d'effet.
  useEffect(() => {
    if (!cfg) return;
    const t = setTimeout(loadPreview, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.illustrate_enabled, cfg?.illustrate_strength, cfg?.watercolor_enabled, cfg?.watercolor_strength]);

  // Tri de la liste par métrique (identique au tableau de bord public /actu)
  const displayed = useMemo(() => {
    if (!sortBy) return items;
    const key = { views: "views", comments: "comment_count", shares: "shares" }[sortBy];
    return [...items].sort((a, b) => (b[key] || 0) - (a[key] || 0));
  }, [items, sortBy]);

  const deleteComment = (c) => {
    if (!window.confirm("Supprimer définitivement ce commentaire (et ses réponses) ?")) return;
    api.delete(`/admin/press-review/comments/${c.id}`)
      .then(() => { setComments((xs) => xs.filter((x) => x.id !== c.id)); toast.success("Commentaire supprimé"); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const restoreComment = (c) => {
    api.post(`/admin/press-review/comments/${c.id}/restore`)
      .then(() => { setComments((xs) => xs.map((x) => x.id === c.id ? { ...x, status: "published", reports: 0 } : x)); toast.success("Commentaire rétabli et de nouveau visible"); loadComments(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const saveSettings = (patch) => {
    setSaving(true);
    api.post("/admin/press-review/settings", patch)
      .then((r) => { setCfg((c) => ({ ...c, ...r.data })); toast.success("Réglages enregistrés"); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setSaving(false));
  };

  const runNow = () => {
    setRunning(true);
    api.post("/admin/press-review/run")
      .then(() => {
        toast.success("Collecte lancée — les articles apparaîtront d'ici ~1 min.");
        setTimeout(loadAll, 45000);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setTimeout(() => setRunning(false), 2000));
  };

  const dedupe = () => {
    if (!window.confirm("Rechercher et supprimer les articles d'actu en double ? Pour chaque groupe, l'article le plus commenté (à défaut le plus ancien) est conservé.")) return;
    setDeduping(true);
    api.post("/admin/press-review/dedupe")
      .then((r) => {
        const n = r.data.removed || 0;
        toast.success(n ? `${n} doublon(s) supprimé(s).` : "Aucun doublon trouvé ✅");
        if (n) loadAll();
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setDeduping(false));
  };

  const generateManual = () => {
    const url = genUrl.trim();
    const description = genDesc.trim();
    if (genMode === "url" && !url) { toast.error("Collez le lien de l'article."); return; }
    if (genMode === "desc" && description.length < 15) { toast.error("Décrivez le sujet (15 caractères minimum)."); return; }
    setGenerating(true);
    const base = genMode === "url" ? { url } : { description };
    const payload = { ...base, instructions: genInstr.trim(), remember: genRemember };
    api.post("/admin/press-review/generate-manual", payload)
      .then((r) => {
        toast.success(`Article créé : « ${r.data.title} »`);
        setGenUrl(""); setGenDesc(""); setGenInstr(""); setGenRemember(false);
        loadAll();
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setGenerating(false));
  };

  const saveStandingInstructions = () => {
    saveSettings({ standing_instructions: (cfg.standing_instructions || "").trim() });
    toast.success("Consignes permanentes enregistrées.");
  };

  const addBlocked = () => {
    const v = newBlocked.trim().toLowerCase();
    if (!v) return;
    const locked = (cfg.blocked_locked || []).map((x) => x.toLowerCase());
    const extras = cfg.blocked_sources || [];
    if (locked.includes(v) || extras.includes(v)) { toast.info("Cette source est déjà bloquée."); setNewBlocked(""); return; }
    saveSettings({ blocked_sources: [...extras, v] });
    setNewBlocked("");
  };

  const removeBlocked = (v) => {
    saveSettings({ blocked_sources: (cfg.blocked_sources || []).filter((x) => x !== v) });
  };

  const addRecommended = () => {
    const v = newRecommended.trim().toLowerCase();
    if (!v) return;
    const rec = cfg.recommended_sources || [];
    if (rec.includes(v)) { toast.info("Cette source est déjà recommandée."); setNewRecommended(""); return; }
    saveSettings({ recommended_sources: [...rec, v] });
    setNewRecommended("");
    setTimeout(loadRecStats, 500);
  };

  const removeRecommended = (v) => {
    saveSettings({ recommended_sources: (cfg.recommended_sources || []).filter((x) => x !== v) });
  };

  const pollReillustrate = () => {
    api.get("/admin/press-review/reillustrate-status").then((r) => {
      setReJob(r.data);
      if (r.data.running) { setTimeout(pollReillustrate, 4000); }
      else {
        setReillustrating(false);
        toast.success(`Illustrations régénérées (${r.data.ok || 0}/${r.data.total || 0}).`);
        loadAll();
      }
    }).catch(() => setReillustrating(false));
  };

  const reillustrateAll = () => {
    if (!window.confirm("Régénérer les illustrations de TOUS les articles d'actu avec le nouveau style (objets / symboles, sans monuments ni rues) ? Cela consomme des crédits IA et peut prendre plusieurs minutes.")) return;
    setReillustrating(true);
    api.post("/admin/press-review/reillustrate-all").then((r) => {
      setReJob({ running: true, total: r.data.total || 0, done: 0, ok: 0 });
      toast.success("Régénération lancée — vous pouvez quitter la page, elle continue en arrière-plan.");
      setTimeout(pollReillustrate, 4000);
    }).catch((e) => { setReillustrating(false); toast.error(formatApiError(e)); });
  };

  const toggleStatus = (p) => {
    const next = p.status === "published" ? "draft" : "published";
    api.patch(`/admin/press-review/${p.id}`, { status: next })
      .then(() => { setItems((xs) => xs.map((x) => x.id === p.id ? { ...x, status: next } : x)); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const remove = (p) => {
    if (!window.confirm(`Supprimer définitivement « ${p.title} » ?`)) return;
    api.delete(`/admin/press-review/${p.id}`)
      .then(() => { setItems((xs) => xs.filter((x) => x.id !== p.id)); toast.success("Supprimé"); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  if (loading || !cfg) return <div className="p-8 text-slate-500">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-press-review">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Rss size={22} className="text-[#E05638]" /> Revue de presse locale
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Collecte RSS + réécriture IA + publication SEO chaque matin à 8h (Chartres & Eure-et-Loir).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} data-testid="press-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            <RefreshCw size={15} /> Actualiser
          </button>
          <button onClick={dedupe} disabled={deduping} data-testid="press-dedupe-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-60">
            {deduping ? <Loader2 size={15} className="animate-spin" /> : <CopyX size={15} />} Supprimer les doublons
          </button>
          <button onClick={runNow} disabled={running} data-testid="press-run-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E05638] text-white text-sm font-semibold hover:bg-[#c8482d] disabled:opacity-60">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Générer maintenant
          </button>
        </div>
      </div>

      {/* Réglages */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">Automatisation</div>
              <div className="text-xs text-slate-500">Publication automatique à 8h chaque matin</div>
            </div>
            <button onClick={() => saveSettings({ enabled: !cfg.enabled })} disabled={saving}
              data-testid="press-toggle-enabled"
              className={`relative w-12 h-7 rounded-full transition-colors ${cfg.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>
          <div className="mt-4">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Articles par jour</label>
            <div className="mt-1 flex items-center gap-2">
              <input type="number" min={1} max={15} defaultValue={cfg.per_day} data-testid="press-per-day"
                onBlur={(e) => { const v = Math.max(1, Math.min(15, parseInt(e.target.value) || 5)); if (v !== cfg.per_day) saveSettings({ per_day: v }); }}
                className="w-20 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <span className="text-xs text-slate-400">max 15</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="press-auto-instructions-block">
            <label className="text-xs font-semibold text-slate-600">
              Consignes pour la prochaine génération automatique
            </label>
            <textarea value={cfg.auto_next_instructions || ""}
              onChange={(e) => setCfg((c) => ({ ...c, auto_next_instructions: e.target.value }))}
              rows={3} data-testid="press-auto-instructions"
              placeholder="Ex : Mets l'accent sur la rentrée scolaire cette semaine. Consulte aussi la presse régionale sans forcément la citer."
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y" />
            <div className="mt-1 text-[11px] text-slate-400">
              Ponctuel : appliqué à la prochaine fournée automatique, puis effacé. Pour des consignes définitives, utilisez « Consignes permanentes » ci-dessous.
            </div>
            <button onClick={() => { saveSettings({ auto_next_instructions: (cfg.auto_next_instructions || "").trim() }); toast.success("Consignes ponctuelles enregistrées."); }}
              disabled={saving} data-testid="press-auto-instructions-save-btn"
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <PenLine size={14} />} Enregistrer
            </button>
          </div>
        </div>

        <div className="p-5 rounded-2xl border border-slate-200 bg-white">
          <div className="font-semibold text-slate-900">Backlinks partenaires</div>
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Débouche Discount 28 (discret)</label>
              <input defaultValue={cfg.backlink_debouche_url} data-testid="press-backlink-debouche"
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== cfg.backlink_debouche_url) saveSettings({ backlink_debouche_url: v }); }}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">PROXIreduc (invitation)</label>
              <input defaultValue={cfg.backlink_proxireduc_url} data-testid="press-backlink-proxireduc"
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== cfg.backlink_proxireduc_url) saveSettings({ backlink_proxireduc_url: v }); }}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>
        </div>
      </div>

      {/* Création manuelle d'un article (lien ou description) */}
      <div className="mt-4 p-5 rounded-2xl border border-sky-200 bg-sky-50" data-testid="press-manual-create-card">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#E05638]" />
          <div className="font-semibold text-slate-900">Créer un article à la demande</div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Collez le lien d'un article OU décrivez un sujet : le générateur recherche d'autres
          sources, les recoupe et les cite, exactement comme pour les articles automatiques.
        </p>
        <div className="mt-3 inline-flex rounded-xl bg-slate-100 p-1" data-testid="press-manual-mode">
          <button onClick={() => setGenMode("url")} data-testid="press-manual-mode-url"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${genMode === "url" ? "bg-white text-[#E05638] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Link2 size={14} /> Depuis un lien
          </button>
          <button onClick={() => setGenMode("desc")} data-testid="press-manual-mode-desc"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${genMode === "desc" ? "bg-white text-[#E05638] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <PenLine size={14} /> En décrivant le sujet
          </button>
        </div>

        {genMode === "url" ? (
          <div className="mt-3">
            <input value={genUrl} onChange={(e) => setGenUrl(e.target.value)}
              data-testid="press-manual-url" placeholder="https://www.lechorepublicain.fr/..."
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <div className="mt-1 text-[11px] text-slate-400">Lien d'un article de presse local (Écho Républicain, actu.fr, France Bleu…).</div>
          </div>
        ) : (
          <div className="mt-3">
            <textarea value={genDesc} onChange={(e) => setGenDesc(e.target.value)} rows={3}
              data-testid="press-manual-desc" placeholder="Ex : Grève des agents territoriaux à Chartres cette semaine, revendications salariales…"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y" />
            <div className="mt-1 text-[11px] text-slate-400">Décrivez le sujet avec des mots-clés précis (lieu, acteurs) pour une meilleure recherche de sources.</div>
          </div>
        )}

        {/* Consignes ponctuelles pour cet article (+ option mémoriser) */}
        <div className="mt-3" data-testid="press-manual-instructions-block">
          <label className="text-xs font-semibold text-slate-600">
            Consignes pour cet article <span className="font-normal text-slate-400">(facultatif)</span>
          </label>
          <textarea value={genInstr} onChange={(e) => setGenInstr(e.target.value)} rows={2}
            data-testid="press-manual-instructions"
            placeholder="Ex : J'ai appris que la réouverture est prévue le 15 septembre. Consulte aussi d'autres sources pour vérifier, sans forcément les citer. Garde un ton neutre."
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y" />
          <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="press-manual-remember-label">
            <input type="checkbox" checked={genRemember} onChange={(e) => setGenRemember(e.target.checked)}
              data-testid="press-manual-remember" className="accent-[#E05638]" />
            Mémoriser ces consignes pour <b>tous</b> les prochains articles
          </label>
        </div>

        <button onClick={generateManual} disabled={generating} data-testid="press-manual-generate-btn"
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E05638] text-white text-sm font-semibold hover:bg-[#c8482d] disabled:opacity-60">
          {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {generating ? "Génération en cours…" : "Générer l'article"}
        </button>
      </div>

      {/* Consignes permanentes de la rédaction (appliquées à TOUS les articles) */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press-standing-card">
        <div className="flex items-center gap-2">
          <PenLine size={18} className="text-[#E05638]" />
          <div className="font-semibold text-slate-900">Consignes permanentes de la rédaction</div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Ces instructions s'appliquent à <b>tous</b> les articles (automatiques et à la demande) :
          ton, angles à éviter, informations récurrentes, sources à consulter sans forcément les citer…
          Elles ne remplacent jamais les règles de fiabilité et de non-copie.
        </p>
        <textarea value={cfg.standing_instructions || ""}
          onChange={(e) => setCfg((c) => ({ ...c, standing_instructions: e.target.value }))}
          rows={4} data-testid="press-standing-instructions"
          placeholder="Ex : Privilégie un ton positif et local. Ne mentionne jamais les noms de victimes. Rappelle le contexte du plan de circulation de Chartres quand c'est pertinent."
          className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y" />
        <button onClick={saveStandingInstructions} disabled={saving} data-testid="press-standing-save-btn"
          className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Enregistrer les consignes
        </button>
      </div>

      {/* Sources interdites (mise en conformité droits d'auteur) */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press-blocked-card">
        <div className="flex items-center gap-2">
          <Ban size={18} className="text-red-600" />
          <div className="font-semibold text-slate-900">Sources interdites</div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Ces médias ne seront jamais récupérés, réécrits ni cités par la revue de presse
          (domaine, URL ou nom). Ajoutez un domaine (ex. <code>lemonde.fr</code>) ou un nom de média.
        </p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="press-blocked-list">
          {(cfg.blocked_locked || []).map((b) => (
            <span key={`lock-${b}`} data-testid={`blocked-locked-${b}`}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 text-xs px-2.5 py-1">
              <Lock size={11} className="text-slate-400" /> {b}
            </span>
          ))}
          {(cfg.blocked_sources || []).map((b) => (
            <span key={`extra-${b}`} data-testid={`blocked-extra-${b}`}
              className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 text-xs px-2.5 py-1">
              {b}
              <button onClick={() => removeBlocked(b)} disabled={saving}
                data-testid={`blocked-remove-${b}`} title="Retirer"
                className="ml-0.5 rounded-full hover:bg-red-100 p-0.5">
                <X size={11} />
              </button>
            </span>
          ))}
          {(cfg.blocked_sources || []).length === 0 && (cfg.blocked_locked || []).length === 0 && (
            <span className="text-xs text-slate-400">Aucune source interdite.</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input value={newBlocked} onChange={(e) => setNewBlocked(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addBlocked(); }}
            data-testid="press-blocked-input" placeholder="ex. lemonde.fr ou Le Parisien"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <button onClick={addBlocked} disabled={saving || !newBlocked.trim()}
            data-testid="press-blocked-add-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 shrink-0">
            <Ban size={15} /> Bloquer
          </button>
        </div>
        <div className="mt-1 text-[11px] text-slate-400">
          <Lock size={10} className="inline mr-0.5" /> Les entrées verrouillées (L'Écho Républicain) sont imposées pour raisons juridiques et ne peuvent pas être retirées.
        </div>
      </div>

      {/* Sources recommandées (médias à parcourir pour trouver des actus) */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press-recommended-card">
        <div className="flex items-center gap-2">
          <Rss size={18} className="text-emerald-600" />
          <div className="font-semibold text-slate-900">Sources recommandées</div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Médias que la revue de presse ira parcourir en plus des flux par défaut pour trouver des
          actualités locales. Ajoutez un domaine (ex. <code>ouest-france.fr</code>, <code>actu.fr</code>)
          ou un mot-clé. La recherche reste filtrée sur l'Eure-et-Loir (Chartres, Dreux…).
        </p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="press-recommended-list">
          {(cfg.recommended_sources || []).map((b) => (
            <span key={`rec-${b}`} data-testid={`recommended-item-${b}`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1">
              {b}
              <span data-testid={`recommended-count-${b}`}
                className="ml-0.5 inline-flex items-center rounded-full bg-emerald-600 text-white text-[10px] font-semibold px-1.5 py-0.5"
                title="Articles apportés ce mois-ci">
                {recStats[b] ?? 0} ce mois-ci
              </span>
              <button onClick={() => removeRecommended(b)} disabled={saving}
                data-testid={`recommended-remove-${b}`} title="Retirer"
                className="ml-0.5 rounded-full hover:bg-emerald-100 p-0.5">
                <X size={11} />
              </button>
            </span>
          ))}
          {(cfg.recommended_sources || []).length === 0 && (
            <span className="text-xs text-slate-400">Aucune source recommandée pour l'instant.</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input value={newRecommended} onChange={(e) => setNewRecommended(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRecommended(); }}
            data-testid="press-recommended-input" placeholder="ex. ouest-france.fr ou actu.fr"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <button onClick={addRecommended} disabled={saving || !newRecommended.trim()}
            data-testid="press-recommended-add-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 shrink-0">
            <Rss size={15} /> Ajouter
          </button>
        </div>
      </div>

      {/* Signature illustrée (gratuite) — pilote aussi les images du blog */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press-illustration-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900">Signature illustrée des images</div>
            <div className="text-xs text-slate-500">Les images sont <b>normales par défaut</b>. Activez cet effet pour transformer les images (revue de presse <b>et</b> blog) en illustrations éditoriales. Gratuit, composition préservée.</div>
          </div>
          <button onClick={() => saveSettings({ illustrate_enabled: !cfg.illustrate_enabled })} disabled={saving}
            data-testid="press-toggle-illustrate"
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${cfg.illustrate_enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${cfg.illustrate_enabled ? "translate-x-5" : ""}`} />
          </button>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Intensité de l'effet</label>
            <span className="text-sm font-bold text-slate-900" data-testid="press-illustrate-strength-value">
              {Math.round((cfg.illustrate_strength ?? 0.5) * 100)}%
            </span>
          </div>
          <input type="range" min={0.25} max={0.7} step={0.05} defaultValue={cfg.illustrate_strength ?? 0.5}
            data-testid="press-illustrate-strength" disabled={!cfg.illustrate_enabled}
            onChange={(e) => setCfg((c) => ({ ...c, illustrate_strength: parseFloat(e.target.value) }))}
            onMouseUp={(e) => saveSettings({ illustrate_strength: parseFloat(e.target.value) })}
            onTouchEnd={(e) => saveSettings({ illustrate_strength: parseFloat(e.target.value) })}
            className="mt-2 w-full accent-[#E05638] disabled:opacity-50" />
          <div className="mt-1 flex justify-between text-[11px] text-slate-400">
            <span>Subtil (proche photo)</span><span>Marqué (très illustré)</span>
          </div>
          <div className="mt-2 text-[11px] text-slate-400">
            S'applique aux nouveaux articles. Pour l'appliquer à un article existant, utilisez « Ré-illustrer » sur sa fiche.
          </div>
        </div>

        {/* Effet aquarelle (revue de presse) — indépendant et cumulable */}
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="press-watercolor-section">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-slate-900">Effet aquarelle</div>
              <div className="text-xs text-slate-500">Donne aux images d'articles un rendu peinture aquarelle (revue de presse). Optionnel, cumulable avec la signature illustrée.</div>
            </div>
            <button onClick={() => saveSettings({ watercolor_enabled: !cfg.watercolor_enabled })} disabled={saving}
              data-testid="press-toggle-watercolor"
              className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${cfg.watercolor_enabled ? "bg-sky-500" : "bg-slate-300"}`}>
              <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${cfg.watercolor_enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Intensité de l'aquarelle</label>
              <span className="text-sm font-bold text-slate-900" data-testid="press-watercolor-strength-value">
                {Math.round((cfg.watercolor_strength ?? 0.5) * 100)}%
              </span>
            </div>
            <input type="range" min={0.25} max={0.7} step={0.05} value={cfg.watercolor_strength ?? 0.5}
              data-testid="press-watercolor-strength" disabled={!cfg.watercolor_enabled}
              onChange={(e) => setCfg((c) => ({ ...c, watercolor_strength: parseFloat(e.target.value) }))}
              onMouseUp={(e) => saveSettings({ watercolor_strength: parseFloat(e.target.value) })}
              onTouchEnd={(e) => saveSettings({ watercolor_strength: parseFloat(e.target.value) })}
              className="mt-2 w-full accent-sky-500 disabled:opacity-50" />
            <div className="mt-1 flex justify-between text-[11px] text-slate-400">
              <span>Léger</span><span>Très aquarelle</span>
            </div>
          </div>
        </div>

        {/* Aperçu « avant / après » en direct de l'effet choisi */}
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="press-preview-section">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-900 text-sm">Aperçu en direct</div>
            <button onClick={loadPreview} disabled={previewLoading} data-testid="press-preview-refresh"
              className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <RefreshCw size={12} className={previewLoading ? "animate-spin" : ""} /> Actualiser
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Visualisez l'effet choisi sur une image d'exemple, sans attendre le prochain article.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <figure className="m-0">
              <img src={preview?.original} alt="Avant"
                data-testid="press-preview-before"
                className="w-full h-32 sm:h-36 object-cover rounded-lg border border-slate-200 bg-slate-100" />
              <figcaption className="mt-1 text-[11px] text-slate-400 text-center">Avant (image normale)</figcaption>
            </figure>
            <figure className="m-0 relative">
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg">
                  <Loader2 size={18} className="animate-spin text-slate-500" />
                </div>
              )}
              <img src={preview?.result || preview?.original} alt="Après"
                data-testid="press-preview-after"
                className="w-full h-32 sm:h-36 object-cover rounded-lg border border-slate-200 bg-slate-100" />
              <figcaption className="mt-1 text-[11px] text-slate-400 text-center">
                Après ({cfg.watercolor_enabled && cfg.illustrate_enabled ? "illustré + aquarelle"
                  : cfg.watercolor_enabled ? "aquarelle"
                  : cfg.illustrate_enabled ? "illustré" : "aucun effet"})
              </figcaption>
            </figure>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="font-semibold text-slate-900 text-sm">Régénérer les anciens articles</div>
          <p className="mt-1 text-xs text-slate-500">
            Recrée l'illustration IA de <b>tous</b> les articles déjà publiés avec le nouveau style
            fonctionnel (objets &amp; symboles, <b>sans monuments ni rues</b>). Consomme des crédits IA.
          </p>
          {reillustrating && reJob && (
            <div className="mt-2" data-testid="reillustrate-progress">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-[#E05638] transition-all"
                  style={{ width: `${reJob.total ? Math.round((reJob.done / reJob.total) * 100) : 0}%` }} />
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {reJob.done || 0} / {reJob.total || 0} article(s) traité(s)…
              </div>
            </div>
          )}
          <button onClick={reillustrateAll} disabled={reillustrating}
            data-testid="press-reillustrate-all-btn"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
            {reillustrating ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {reillustrating ? "Régénération en cours…" : "Régénérer les illustrations des anciens articles"}
          </button>
        </div>
      </div>

      {cfg.last_auto_at && (
        <div className="mt-4 text-sm text-slate-500">
          Dernière génération : <b>{new Date(cfg.last_auto_at).toLocaleString("fr-FR")}</b>
          {" · "}Aujourd'hui : <b>{cfg.today_count}</b> article(s)
          {cfg.next_run_at && <> · Prochain passage : <b>{new Date(cfg.next_run_at).toLocaleString("fr-FR")}</b></>}
        </div>
      )}

      {/* Tableau de bord performance (30 jours) — identique à /actu */}
      {stats && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="admin-press-dashboard">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Performance · {stats.articles} article(s) sur 30 jours
            </div>
            {sortBy && (
              <button onClick={() => setSortBy(null)} data-testid="admin-press-sort-reset"
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#E05638]">
                <X size={12} /> Tri actif : {sortBy === "views" ? "ouvertures" : sortBy === "comments" ? "commentaires" : "partages"} — réinitialiser
              </button>
            )}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <button onClick={() => setSortBy(sortBy === "views" ? null : "views")} data-testid="admin-press-dash-views"
              className={`rounded-xl p-3 text-center transition-all ${sortBy === "views" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`}>
              <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><Eye size={13} /> Ouvertures</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{stats.views}</div>
            </button>
            <button onClick={() => setSortBy(sortBy === "comments" ? null : "comments")} data-testid="admin-press-dash-comments"
              className={`rounded-xl p-3 text-center transition-all ${sortBy === "comments" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`}>
              <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><MessageSquare size={13} /> Commentaires</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{stats.comments}</div>
            </button>
            <button onClick={() => setSortBy(sortBy === "shares" ? null : "shares")} data-testid="admin-press-dash-shares"
              className={`rounded-xl p-3 text-center transition-all ${sortBy === "shares" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`}>
              <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><Share2 size={13} /> Partages</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{stats.shares}</div>
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      <h2 className="mt-8 font-heading text-lg font-bold text-slate-900">Articles ({items.length})</h2>
      <div className="mt-3 space-y-2" data-testid="press-admin-list">
        {items.length === 0 && <div className="text-sm text-slate-500 py-6">Aucun article pour le moment. Cliquez sur « Générer maintenant » pour tester.</div>}
        {displayed.map((p) => (
          <div key={p.id} data-testid={`press-admin-item-${p.id}`}
            className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
            <div className="w-16 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 text-sm truncate">{p.title}</div>
              <div className="text-xs text-slate-500 truncate">
                {p.city} · {p.source_name}
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {p.status === "published" ? "En ligne" : "Masqué"}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" title="Ouvertures" data-testid={`admin-press-stat-views-${p.slug}`}>
                  <Eye size={11} /> {p.views || 0}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" title="Commentaires" data-testid={`admin-press-stat-comments-${p.slug}`}>
                  <MessageSquare size={11} /> {p.comment_count || 0}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" title="Partages" data-testid={`admin-press-stat-shares-${p.slug}`}>
                  <Share2 size={11} /> {p.shares || 0}
                </span>
              </div>
            </div>
            <a href={`/actu/${p.slug}`} target="_blank" rel="noopener noreferrer"
              className="p-2 rounded-lg text-slate-400 hover:text-[#E05638] hover:bg-slate-50" title="Voir">
              <ExternalLink size={16} />
            </a>
            <button onClick={() => toggleStatus(p)} data-testid={`press-toggle-${p.id}`}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50" title={p.status === "published" ? "Masquer" : "Publier"}>
              {p.status === "published" ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            <button onClick={() => remove(p)} data-testid={`press-delete-${p.id}`}
              className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Supprimer">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Modération des commentaires */}
      <div className="mt-10 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare size={18} className="text-[#E05638]" /> Modération des commentaires
        </h2>
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => { const v = true; setOnlyReported(v); loadComments(v); }} data-testid="mod-filter-reported"
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${onlyReported ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            <Flag size={13} className="inline mr-1" /> Signalés
          </button>
          <button onClick={() => { const v = false; setOnlyReported(v); loadComments(v); }} data-testid="mod-filter-all"
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${!onlyReported ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            Tous
          </button>
        </div>
      </div>
      <div className="mt-3 space-y-2" data-testid="press-comments-mod-list">
        {comments.length === 0 && (
          <div className="text-sm text-slate-500 py-6" data-testid="mod-empty">
            {onlyReported ? "Aucun commentaire signalé. Tout est calme ✅" : "Aucun commentaire pour le moment."}
          </div>
        )}
        {comments.map((c) => (
          <div key={c.id} data-testid={`mod-comment-${c.id}`}
            className={`flex items-start gap-3 p-3 rounded-xl border bg-white ${c.reports > 0 ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
                <span className="font-semibold text-slate-800">{c.author_name}</span>
                {c.parent_id && <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">réponse</span>}
                {c.status === "flagged" && <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold">masqué</span>}
                {c.reports > 0 && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold inline-flex items-center gap-1"><Flag size={10} /> {c.reports} signalement(s)</span>}
                {c.likes > 0 && <span className="inline-flex items-center gap-1 text-slate-400"><Heart size={11} /> {c.likes}</span>}
                {c.created_at && <span>{new Date(c.created_at).toLocaleString("fr-FR")}</span>}
              </div>
              <p className="mt-1 text-sm text-slate-700 break-words">{c.text}</p>
            </div>
            <a href={`/actu/${c.post_slug}`} target="_blank" rel="noopener noreferrer"
              className="p-2 rounded-lg text-slate-400 hover:text-[#E05638] hover:bg-slate-50 shrink-0" title="Voir l'article">
              <ExternalLink size={16} />
            </a>
            {(c.status === "flagged" || c.reports > 0) && (
              <button onClick={() => restoreComment(c)} data-testid={`mod-restore-${c.id}`}
                className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 shrink-0" title="Rétablir (garder)">
                <Eye size={16} />
              </button>
            )}
            <button onClick={() => deleteComment(c)} data-testid={`mod-delete-${c.id}`}
              className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0" title="Supprimer">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
